import {ReactNode, useEffect, useRef} from "react";
import {useFormContext} from "react-hook-form";
import * as Yup from "yup";

import {Alert} from "@/components/alert/Alert.tsx";
import {ContactsSelectField} from "@/components/form/fields/ContactsSelectField/ContactsSelectField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {Select} from "@/components/select/Select.tsx";
import {
  Account,
  AccountItems,
  TwoFactorOverride,
  TwoFactorOverrideLabels,
  UserAccessLevel,
  UserAccessLevelLabels,
} from "@/lib/types/accounts.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {ContactGroupBaseItems} from "@/lib/types/contacts.ts";

export interface UserFormValues {
  id?: number;
  first_name: string;
  last_name: string;
  email: string;
  access_level: UserAccessLevel;
  is_api_enabled: boolean;
  is_mobile_enabled: boolean;
  has_purchase_permission: boolean;
  require_two_factor: TwoFactorOverride;
  notify_paid_invoices: boolean;
  notify_important_updates: boolean;
  contact_groups?: number[];
  assigned_subaccounts: number[];
}

export type FormValuesKey = keyof UserFormValues;

export const EditUserInfoSchema = Yup.object({
  id: Yup.number().optional(),
  first_name: Yup.string().required("This field cannot be blank."),
  last_name: Yup.string().required("This field cannot be blank."),
  email: Yup.string().required("This field cannot be blank."),
  access_level: Yup.string<UserAccessLevel>().required(),
  assigned_subaccounts: Yup.array().of(Yup.number().required()).required(),
  contact_groups: Yup.array().of(Yup.number().required()),
  is_api_enabled: Yup.boolean().required(),
  is_mobile_enabled: Yup.boolean().required(),
  has_purchase_permission: Yup.boolean().required(),
  require_two_factor: Yup.string<TwoFactorOverride>().required(),
  notify_paid_invoices: Yup.boolean().required(),
  notify_important_updates: Yup.boolean().required(),
});

interface EditUserInfoFormProps {
  className?: string;
  serverErrors?: ServerErrors;
  accountAccounts: AccountItems;
  availableContactGroups: ContactGroupBaseItems;
  accountHasPurchasePermission: boolean;
  isRegularAccount: boolean;
  isEditingPrimaryUser: boolean;
}

const EditUserFormBasic = ({
  className,
  serverErrors,
  accountAccounts,
  availableContactGroups,
  accountHasPurchasePermission,
  isRegularAccount,
  isEditingPrimaryUser,
}: EditUserInfoFormProps) => {
  const {
    setValue,
    setError,
    watch,
    formState: {defaultValues},
  } = useFormContext<UserFormValues>();
  const purchasePermissionOverride = useRef<boolean | undefined>(
    defaultValues?.has_purchase_permission === (defaultValues?.access_level === UserAccessLevel.ADMIN)
      ? undefined
      : defaultValues?.has_purchase_permission
  );
  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => {
          setError(key as FormValuesKey, {message: serverErrors[key][0]});
        });
    }
  }, [setError, serverErrors]);

  const isPurchaseDisabled =
    isEditingPrimaryUser || ![UserAccessLevel.ADMIN, UserAccessLevel.WRITE].includes(watch("access_level"));

  const inputFields: ReactNode[] = [
    <InputField name="first_name" label="First Name" required={true} />,
    <InputField name="last_name" label="Last Name" required={true} />,
    <InputField name="email" label="Email" required={true} />,
    <SelectField
      name="access_level"
      label="Access Level"
      options={Object.keys(UserAccessLevelLabels).map((key) => ({
        value: key,
        label: UserAccessLevelLabels[key as UserAccessLevel] as string,
      }))}
      onChange={(accessLevel) => {
        if (!isRegularAccount || isEditingPrimaryUser) {
          return;
        }
        const hasPurchasePermission = purchasePermissionOverride.current ?? accessLevel === UserAccessLevel.ADMIN;
        setValue("has_purchase_permission", accessLevel !== UserAccessLevel.READ && hasPurchasePermission);
      }}
    />,
  ];

  if (accountAccounts.items.length > 0) {
    inputFields.push(
      <>
        <SelectField
          required
          tooltip={"Which Subaccounts this user may access. Admins can always access all Subaccounts."}
          name={"assigned_subaccounts"}
          label="Subaccounts"
          select={
            <Select<Account, true>
              options={accountAccounts.items}
              onChange={(values) => {
                setValue(
                  "assigned_subaccounts",
                  values.map((selectedAccount) => selectedAccount.id)
                );
              }}
              value={
                watch("access_level") === UserAccessLevel.ADMIN
                  ? []
                  : accountAccounts.items.filter((a) => watch("assigned_subaccounts").includes(a.id))
              }
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              isSearchable
              isMulti
              isDisabled={watch("access_level") === UserAccessLevel.ADMIN}
              placeholder="All Subaccounts"
            />
          }
        />
      </>
    );
  }

  if (!watch("id")) {
    inputFields.push(
      <ContactsSelectField
        contacts={availableContactGroups.items}
        name={"contact_groups"}
        label="Add to Contacts"
        required={false}
      />
    );
  }

  inputFields.push(
    <>
      <SelectField
        name="require_two_factor"
        label="2FA Override"
        tooltip="Assign settings to user which override account 2FA requirements."
        options={Object.keys(TwoFactorOverrideLabels).map((key) => ({
          value: key,
          label: TwoFactorOverrideLabels[key as TwoFactorOverride] as string,
        }))}
      />
    </>
  );

  return (
    <div className={className}>
      {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}
      <div className="row row-cols-2 mb-3">
        {inputFields.map((elem, index) => (
          <div key={index} className="col-6 mb-3">
            {elem}
          </div>
        ))}
      </div>
      <div className="row mb-3">
        <div className="col-12 d-flex flex-column gap-2">
          <SwitchField name="is_api_enabled" label="Enable API access" />
          <SwitchField name="is_mobile_enabled" label="Enable Mobile App access" />
          <SwitchField name="notify_paid_invoices" label="Send new invoices via email" />
          <SwitchField name="notify_important_updates" label="Notify me of important updates" />
          {accountHasPurchasePermission && (
            <SwitchField
              name="has_purchase_permission"
              label="Allow add-on purchase"
              disabled={isPurchaseDisabled}
              onChange={(hasPurchasePermission) => {
                purchasePermissionOverride.current = hasPurchasePermission;
              }}
            />
          )}
        </div>
      </div>
      {!watch("id") && (
        <Alert variant={"maintenance"}>
          An email invite will be sent to this address with a link for creating a link for creating a password.
        </Alert>
      )}
    </div>
  );
};

export default EditUserFormBasic;
